import { Typography } from "@mui/material";
import React, { useRef, useEffect } from "react";

export default function Neuron({
  width = "100%",
  height = "50%",
  background = "#06253aff",
  neuronColor = "#98f2ff",
  sparkColor = "#9be6a5",
  density = 0.0008,
  maxConnections = 3,
  maxSpeed = 0.5,
  counter = 0,
  glow = true,
} = {}) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const resizeObserverRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    let DPR = Math.max(1, window.devicePixelRatio || 1);

    function setSize() {
      const rect = canvas.getBoundingClientRect();
      canvas.width = Math.round(rect.width * DPR);
      canvas.height = Math.round(rect.height * DPR);
      canvas.style.width = rect.width + "px";
      canvas.style.height = rect.height + "px";
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    }

    setSize();

    if (typeof ResizeObserver !== "undefined") {
      resizeObserverRef.current = new ResizeObserver(() => {
        DPR = Math.max(1, window.devicePixelRatio || 1);
        setSize();
        init();
      });
      resizeObserverRef.current.observe(canvas);
    } else {
      window.addEventListener("resize", () => {
        DPR = Math.max(1, window.devicePixelRatio || 1);
        setSize();
        init();
      });
    }

    let nodes = [];
    let sparks = [];
    let widthPx = canvas.width / DPR;
    let heightPx = canvas.height / DPR;

    function rand(min, max) {
      return Math.random() * (max - min) + min;
    }

    function distance(a, b) {
      const dx = a.x - b.x;
      const dy = a.y - b.y;
      return Math.sqrt(dx * dx + dy * dy);
    }

    function Node(x, y) {
      this.x = x;
      this.y = y;
      this.vx = rand(-maxSpeed, maxSpeed);
      this.vy = rand(-maxSpeed, maxSpeed);
      this.r = rand(0.9, 2.2);
      this.energy = 0;
      this.cooldown = 0;
      this.threshold = 0.9;
      this.connections = [];
      this.fired = false;
    }

    Node.prototype.update = function (dt) {
      this.x += this.vx * dt * Math.sin(counter * 0.01) * 20;
      this.y += this.vy * dt;

      if (this.x < 0 || this.x > widthPx) this.vx *= -1;
      if (this.y < 0 || this.y > heightPx) this.vy *= -1;

      if (this.energy > this.threshold && !this.fired) {
        this.fire();
      }
    };

    Node.prototype.fire = function () {
      this.fired = true;
      sparks.push({ x: this.x, y: this.y, life: 1.0, size: this.r * 5 });
      setTimeout(() => {
        for (let c of this.connections) {
          if (!c.fired) {
            c.energy = 1.0;
          }
        }
      }, 50);
    };

    function init() {
      widthPx = canvas.width / DPR;
      heightPx = canvas.height / DPR;
      nodes = [];
      sparks = [];

      const targetCount = Math.max(6, Math.floor(widthPx * heightPx * density));

      for (let i = 0; i < targetCount; i++) {
        nodes.push(new Node(rand(0, widthPx), rand(0, heightPx)));
      }

      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i];
        const dists = [];
        for (let j = 0; j < nodes.length; j++) {
          if (i === j) continue;
          dists.push({ node: nodes[j], d: (a.x - nodes[j].x) ** 2 + (a.y - nodes[j].y) ** 2 });
        }
        dists.sort((p, q) => p.d - q.d);
        a.connections = dists.slice(0, maxConnections).map((p) => p.node);
      }

      // start with one neuron firing
      nodes[Math.floor(Math.random() * nodes.length)].energy = 1.0;
    }

    function clear() {
      ctx.globalCompositeOperation = "source-over";
      ctx.fillStyle = background;
      ctx.fillRect(0, 0, widthPx, heightPx);
      ctx.fillStyle = "rgba(0,0,0,0.12)";
      ctx.fillRect(0, 0, widthPx, heightPx);
    }

    function render() {
      if (glow) ctx.globalCompositeOperation = "lighter";

      for (let n of nodes) {
        for (let c of n.connections) {
          const dist = distance(n, c);
          const alpha = Math.max(0, 0.35 - dist / (Math.max(widthPx, heightPx) * 0.5));
          if (alpha <= 0) continue;
          ctx.beginPath();
          ctx.moveTo(n.x, n.y);
          ctx.lineTo(c.x, c.y);
          ctx.lineWidth = 0.6;
          ctx.strokeStyle = `rgba(152,242,255,${alpha})`;
          ctx.stroke();
        }
      }

      for (let n of nodes) {
        const e = n.fired ? 1 : 0;
        const gsize = n.r * (2 + e * 6);
        ctx.beginPath();
        const g = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, gsize);
        g.addColorStop(0, `rgba(152,242,255,${0.9})`);
        g.addColorStop(0.4, `rgba(152,242,255,${0.18})`);
        g.addColorStop(1, `rgba(10,10,12,0)`);
        ctx.fillStyle = g;
        ctx.fillRect(n.x - gsize, n.y - gsize, gsize * 2, gsize * 2);

        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r + e * 2.4, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${0.9})`;
        ctx.fill();
      }

      for (let i = sparks.length - 1; i >= 0; i--) {
        const s = sparks[i];
        s.life -= 0.025;
        if (s.life <= 0) {
          sparks.splice(i, 1);
          continue;
        }
        const size = s.size * (1 + (1 - s.life) * 0.8);
        const alpha = Math.pow(s.life, 1.6);
        const g = ctx.createRadialGradient(s.x, s.y, 0, s.x, s.y, size);
        g.addColorStop(0, `rgba(155,230,165,${alpha})`);
        g.addColorStop(0.45, `rgba(155,230,165,${Math.min(0.28, alpha * 0.6)})`);
        g.addColorStop(1, `rgba(10,10,12,0)`);
        ctx.fillStyle = g;
        ctx.fillRect(s.x - size, s.y - size, size * 2, size * 2);
      }

      ctx.globalCompositeOperation = "source-over";
    }

    let last = performance.now() / 1000;
    function loop(now) {
      const t = now / 1000;
      let dt = Math.min(0.06, t - last);
      last = t;

      for (let n of nodes) n.update(dt);
      counter++;

      clear();
      render();

      rafRef.current = requestAnimationFrame(loop);
    }

    init();
    rafRef.current = requestAnimationFrame(loop);

    // randomly restart neurons
    setInterval(() => {
        const randomNode = nodes[Math.floor(Math.random() * nodes.length)];
        if (!randomNode.fired) {
            randomNode.energy = 1.0;
            randomNode.fired = false; // reset firing state
        }
        }, 5000);        

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (resizeObserverRef.current) resizeObserverRef.current.disconnect();
    };
  }, [background, counter, density, glow, maxConnections, maxSpeed, neuronColor, sparkColor]);

  return (
    <div
      className="w-full overflow-hidden"
      style={{
        width,
        height,
        background,
        position: "relative",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Typography
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          color: "white",
          fontSize: "1.8rem",
          opacity: 0.6,
          zIndex: 20,
          pointerEvents: "none",
          textAlign: "center",
          width: "100%",
        }}
      >
        {/* AI for Robots and Autonomous Vehicles */}
      </Typography>
      <canvas
        ref={canvasRef}
        style={{
          display: "block",
          width: "100%",
          height: "80%",
        }}
      />
    </div>
  );
}
